import {
  HARNESS_SDK_KEY,
  UNIFIED_LOGIN_BROKER_URL,
  UNIFIED_LOGIN_TENANT_CLIENT_ID,
  UNIFIED_LOGIN_TENANT_ISSUER,
} from '../../constants';
import { HARNESS_FLAGS } from './harnessFlags';
import useHarnessFlag, { UseHarnessFlagReturn } from './useHarnessFlag';

/**
 * Backstop for how long a caller waits on the Harness decision before giving
 * up and taking the legacy path.
 *
 * Capped below the app-wide `AUTH_TIMEOUT_MS` (5s) so the decision always
 * fails open before the auth spinner gives up — an invariant asserted in this
 * hook's tests. That cap sits under Split's ~5.7s p99 cold-cache readiness, so
 * a cold first load can time out here and take the legacy path; inbound SSO
 * then needs a second load. It fails in the safe direction, and raising it
 * would mean raising the app-wide auth budget for every candidate.
 */
export const UNIFIED_LOGIN_DECISION_TIMEOUT_MS = 4000;

export type UnifiedLoginDecision = {
  /** Whether the login-redirect flag has resolved. */
  kickoffReady: boolean;
  /** Send login entries to the broker (`acdx_cp_unified_login`). */
  enabled: boolean;
  /** Whether the inbound session-management flag has resolved. */
  isReady: boolean;
  /** Accept an inbound broker session (`acdx_cp_unified_login_session_mgmt`). */
  sessionMgmtEnabled: boolean;
};

const isConfigured = (): boolean =>
  Boolean(HARNESS_SDK_KEY) &&
  Boolean(UNIFIED_LOGIN_BROKER_URL) &&
  Boolean(UNIFIED_LOGIN_TENANT_ISSUER) &&
  Boolean(UNIFIED_LOGIN_TENANT_CLIENT_ID);

const isFlagOn = ({
  treatment,
  isResolved,
  isTimedOut,
}: UseHarnessFlagReturn): boolean =>
  isResolved && !isTimedOut && treatment === 'on';

const useUnifiedLoginEnabled = (): UnifiedLoginDecision => {
  const kickoff = useHarnessFlag({ flagKey: HARNESS_FLAGS.UNIFIED_LOGIN });
  const sessionMgmt = useHarnessFlag({
    flagKey: HARNESS_FLAGS.UNIFIED_LOGIN_SESSION_MGMT,
  });

  if (!isConfigured()) {
    return {
      kickoffReady: true,
      enabled: false,
      isReady: true,
      sessionMgmtEnabled: false,
    };
  }

  return {
    kickoffReady: kickoff.isResolved,
    enabled: isFlagOn(kickoff),
    isReady: sessionMgmt.isResolved,
    sessionMgmtEnabled: isFlagOn(sessionMgmt),
  };
};

export default useUnifiedLoginEnabled;
